import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { ElasticFormModal, MenuTab, MenuTabs } from 'basic-components'
import { ArrowLeft } from 'basic-components/icons'
import Query from 'dashboard-services/api/Query'

import { groupDialogDatalakeActions } from 'actions/modules/datalake'
import getApiConfig from 'api/ApiConfig'
import { groupsDatalakeQueries } from 'queries/modules/datalake'
import { sendEvent } from 'services/GAService'

import Advanced from './Advanced'
import Details from './Details'
import groupConfigService from './groupConfigService'
import FirstStep from './Share/FirstStep'
import SecondStep from './Share/SecondStep'
import SelectedFooter from './Share/SelectedFooter'
import useGroupDialogAdvancedModule from './hooks/useGroupDialogAdvanced'
import useGroupDialogDetailsModule from './hooks/useGroupDialogDetails'
import useGroupDialogShareModule from './hooks/useGroupDialogShare'
import useGroupDialogSubmitModule from './hooks/useGroupDialogSubmit'

import classNames from 'classnames'
import { When } from 'react-if'

import './GroupDialog.scss'

const { MODES, TABS, DEFAULT_TAB } = groupDialogDatalakeActions
const { useGroupDialogAdvanced } = useGroupDialogAdvancedModule
const { useGroupDialogDetails } = useGroupDialogDetailsModule
const { useGroupDialogShare, SECOND } = useGroupDialogShareModule
const { useGroupDialogSubmit } = useGroupDialogSubmitModule
const EMPTY_ARRAY = []
const EMPTY_GROUP = { groupName: "", description: "", fields: EMPTY_ARRAY, groupTags: EMPTY_ARRAY }
const WITHOUT_POLLING = () => true
const SHARE_EVENT = { category: "view_item", action: "share_group", label: "share" }

const useInitialGroup = ({ isCreate, groupName, description, fields, groupTags, enabled }) => {
  const dispatch = useDispatch(),
        shouldLoadGroup = !isCreate && fields === undefined,
        getQueryBuilder = useCallback(() =>
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
        , [dispatch]),
        { data: loadedGroup } = groupsDatalakeQueries.useGetGroup({ groupName: shouldLoadGroup && enabled ? groupName : undefined, getQueryBuilder, shouldBeLimited: WITHOUT_POLLING }),
        initialGroup = useMemo(() => {
          if(isCreate) {
            return EMPTY_GROUP
          }
          if(!shouldLoadGroup) {
            return { groupName, description, fields, groupTags }
          }
          return loadedGroup
            ? { groupName, description: loadedGroup.description, fields: loadedGroup.fields, groupTags: loadedGroup.groupTags }
            : undefined
        }, [description, fields, groupName, groupTags, isCreate, loadedGroup, shouldLoadGroup]),
        toReturn = useMemo(() => ({ initialGroup }), [initialGroup])

  return toReturn
}

const useSharedWith = ({ isCreate, groupName, enabled }) => {
  const dispatch = useDispatch(),
        getQueryBuilder = useCallback(() => new Query.Builder(dispatch(getApiConfig())), [dispatch]),
        { data, isFetchedAfterMount } = groupsDatalakeQueries.useGetGroupSharedWith({ getQueryBuilder, groupName, enabled: !isCreate && enabled })
  if(isCreate) {
    return EMPTY_ARRAY
  }
  return isFetchedAfterMount ? data : undefined
}

const useVisitedTabs = ({ selectedTab }) => {
  const [visitedTabs, setVisitedTabs] = useState(() => [selectedTab])

  useEffect(() => {
    setVisitedTabs(tabs => tabs.includes(selectedTab) ? tabs : tabs.concat(selectedTab))
  }, [selectedTab])

  return visitedTabs
}

const useShareEvent = ({ isShareVisited }) => {
  const dispatch = useDispatch()

  useEffect(() => {
    isShareVisited && dispatch(sendEvent(SHARE_EVENT))
  }, [dispatch, isShareVisited])
}

const useMenuPortalTarget = () => {
  const [menuPortalTarget, setMenuPortalTarget] = useState()

  useEffect(() => {
    setMenuPortalTarget(document.querySelector(".data-lake__group-dialog .ng-elastic-modal__window__content") || undefined)
  }, [])

  return menuPortalTarget
}

const GroupDialog = () => {
  const dispatch = useDispatch(),
        { mode, tab, groupName, description, fields, groupTags, canBeShared } = useSelector(state => state.datalakeState.groupDialogState),
        isCreate = mode === MODES.CREATE,
        [selectedTab, setSelectedTab] = useState(tab || DEFAULT_TAB[mode]),
        visitedTabs = useVisitedTabs({ selectedTab }),
        isAdvancedVisited = visitedTabs.includes(TABS.ADVANCED),
        isShareVisited = visitedTabs.includes(TABS.SHARE),
        menuTabsRef = useRef(),
        menuPortalTarget = useMenuPortalTarget(),
        { initialGroup } = useInitialGroup({ isCreate, groupName, description, fields, groupTags, enabled: isAdvancedVisited || visitedTabs.includes(TABS.DETAILS) }),
        details = useGroupDialogDetails({ initialGroup }),
        advanced = useGroupDialogAdvanced({ isCreate, groupName: isCreate ? "" : groupName, initialFields: initialGroup?.fields, isEnabled: isAdvancedVisited }),
        sharedWith = useSharedWith({ isCreate, groupName, enabled: isShareVisited }),
        shareGroupName = isCreate ? String(details.groupName || "").trim() : groupName,
        share = useGroupDialogShare({ groupName: shareGroupName, sharedWith }),
        isSecondStep = share.step === SECOND,
        isDetailsLoading = !details.isInitialized,
        isAdvancedLoading = isDetailsLoading || !advanced.isLoaded,
        isShareDisabled = !canBeShared || !share.isInitialized,

        onClose = useCallback(() => dispatch(groupDialogDatalakeActions.close()), [dispatch]),
        { onSubmit: onSave, isCreated } = useGroupDialogSubmit({ mode, groupName, details, advanced, share, menuTabsRef }),
        onApply = useCallback(event => {
          // The re-render turns this button into the form's submit button before the click's default action runs
          event?.preventDefault()
          share.addUsers()
        }, [share]),
        secondStepSubmitProps = useMemo(() => ({ type: "button", onClick: onApply }), [onApply]),
        title = useMemo(() => {
          if(isSecondStep) {
            return (
              <div className="data-lake__group-dialog__title">
                <div
                    className="data-lake__group-dialog__title__back"
                    data-testid="group-dialog-back"
                    onClick={share.goBack}
                >
                  <ArrowLeft
                      color="blue-bright"
                      height={16}
                      width={16}
                  />
                </div>
                <div className="data-lake__group-dialog__title__text">Edit access</div>
              </div>
            )
          }
          return isCreate ? "Create group" : `Edit group ${groupName}`
        }, [groupName, isCreate, isSecondStep, share.goBack]),
        footerContent = useMemo(() => (
          <div className="data-lake__group-dialog__footer-content">
            <When condition={!isSecondStep && share.selectedUsers.length > 0}>
              {() => (
                <SelectedFooter
                    editSelectedUsers={share.editSelectedUsers}
                    removeSelectedUsers={share.removeSelectedUsers}
                    selectedUsersLength={share.selectedUsers.length}
                />
              )}
            </When>
          </div>
        ), [isSecondStep, share.editSelectedUsers, share.removeSelectedUsers, share.selectedUsers.length]),
        shouldShowFooterContent = isSecondStep || selectedTab === TABS.SHARE,
        isSelectingUsers = !isSecondStep && selectedTab === TABS.SHARE && share.selectedUsers.length > 0

  useShareEvent({ isShareVisited })

  return (
    <ElasticFormModal
        additionalFooterContent={shouldShowFooterContent ? footerContent : <Fragment/>}
        className={classNames("data-lake__group-dialog", { "data-lake__group-dialog--is-selecting-users": isSelectingUsers })}
        closeOnBackground={false}
        onClose={isSecondStep ? share.goBack : onClose}
        onSubmit={isSecondStep ? share.addUsers : onSave}
        submitProps={isSecondStep ? secondStepSubmitProps : undefined}
        submitText={isSecondStep ? "Apply" : "Save"}
        title={title}
    >
      {isSecondStep ? (
        <SecondStep
            addUserSelected={share.addUserSelected}
            menuPortalTarget={menuPortalTarget}
            ref={share.secondStepRef}
            withDataRange
        />
      ) : (
        <MenuTabs
            defaultSelected={selectedTab}
            mode="compact"
            onChange={setSelectedTab}
            ref={menuTabsRef}
        >
          <MenuTab name={TABS.DETAILS}>
            <Details
                description={details.description}
                errors={details.errors}
                fieldsToDisplay={details.fieldsToDisplay}
                groupName={details.groupName}
                groupTags={details.groupTags}
                isDataTimeFrameEnabled={details.isDataTimeFrameEnabled}
                isLoading={isDetailsLoading}
                isNameDisabled={!isCreate || isCreated}
                menuPortalTarget={menuPortalTarget}
                onAddField={details.onAddField}
                onChangeDataTimeFrame={details.onChangeDataTimeFrame}
                onChangeDescription={details.onChangeDescription}
                onChangeField={details.onChangeField}
                onChangeForwardCurves={details.onChangeForwardCurves}
                onChangeGroupName={details.onChangeGroupName}
                onChangeGroupTags={details.onChangeGroupTags}
                onRemoveField={details.onRemoveField}
                shouldBeForwardCurvesGroup={details.shouldBeForwardCurvesGroup}
            />
          </MenuTab>
          <MenuTab name={TABS.ADVANCED}>
            <Advanced
                dynamicValidation={advanced.dynamicValidation}
                errors={details.errors}
                groupConfig={advanced.groupConfig || groupConfigService.EMPTY_GROUP_CONFIG}
                isLoading={isAdvancedLoading}
                menuPortalTarget={menuPortalTarget}
                onChangePriority={advanced.onChangePriority}
                onChangeValidationOptions={advanced.onChangeValidationOptions}
                priority={advanced.priority}
                setDynamicValidation={advanced.setDynamicValidation}
                setGroupConfig={advanced.setGroupConfig}
                validationOptions={advanced.validationOptions}
                validatorTask={advanced.validatorTask}
            />
          </MenuTab>
          <MenuTab name={TABS.SHARE}>
            <FirstStep
                addUserSelected={share.addUserSelected}
                disabled={isShareDisabled}
                menuPortalTarget={menuPortalTarget}
                onAccessChange={share.onAccessChange}
                onAddUserChange={share.onAddUserChange}
                onAddUserClose={share.onAddUserClose}
                onUserCheck={share.onUserCheck}
                selectedUsers={share.selectedUsers}
                sharedWith={share.sharedWith}
            />
          </MenuTab>
        </MenuTabs>
      )}
    </ElasticFormModal>
  )
}

export default memo(GroupDialog)
